"use client";

import { c as _c } from "react/compiler-runtime";
import { usePricing } from "@/components/pricing/PricingProvider";
import React, { useCallback, useTransition } from "react";
import { Heart, Share2 } from "lucide-react";
import { sendGTMEvent } from "@next/third-parties/google";
import type { DealerInfo, VehicleDetails } from "@/types/vehicle-details";
import { useIsVehicleSaved, useSavedVehicles } from "@/hooks/useSavedVehicles";
import { createShareEventData, createWishlistEventData, type AnalyticsContext, type VehicleData } from "@/utils/ga4-events";
import { formatCurrency } from "@/utils/pricing";
import styles from "../VehicleDetailsPage.module.css";
interface VehicleActionsProps {
  vehicleData: VehicleDetails;
  dealerData?: DealerInfo | null;
}
function useVehicleActions(vehicleData: VehicleDetails, dealerData?: DealerInfo | null) {
  const {
    getNoHagglePricing
  } = usePricing();
  const [isPending, startTransition] = useTransition();
  const isSaved = useIsVehicleSaved(vehicleData.vin);
  const {
    toggleSave
  } = useSavedVehicles();
  const vehicleName = `${vehicleData.year || ""} ${vehicleData.make || ""} ${vehicleData.model || ""} ${vehicleData.trim || ""}`.trim();
  const pricing = getNoHagglePricing(vehicleData.price);
  const analyticsVehicleData: VehicleData = {
    vin: vehicleData.vin,
    name: vehicleName,
    price: pricing?.noHagglePrice ?? vehicleData.price,
    make: vehicleData.make,
    model: vehicleData.model,
    year: vehicleData.year
  };
  const analyticsContext: AnalyticsContext = {
    buttonLocation: "hero"
  };
  const handleSave = useCallback(() => {
    startTransition(async () => {
      const success = await toggleSave(vehicleData);
      if (!success) return;
      sendGTMEvent({
        event: isSaved ? "remove_from_wishlist" : "add_to_wishlist",
        ...createWishlistEventData(analyticsVehicleData, analyticsContext, dealerData || undefined)
      });
    });
  }, [analyticsVehicleData, dealerData, isSaved, toggleSave, vehicleData]);
  const handleShare = useCallback(async () => {
    const shareData = {
      title: vehicleName,
      text: `Check out this ${vehicleName}${pricing ? ` for ${formatCurrency(pricing.noHagglePrice)}` : ""}`,
      url: window.location.href
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        sendGTMEvent({
          event: "share",
          ...createShareEventData("native", analyticsVehicleData, analyticsContext, dealerData || undefined)
        });
        return;
      }
      await navigator.clipboard.writeText(window.location.href);
      sendGTMEvent({
        event: "share",
        ...createShareEventData("clipboard", analyticsVehicleData, analyticsContext, dealerData || undefined)
      });
    } catch (error) {
      // Native share reports cancellation as an exception. The page stays usable.
      console.log("Vehicle share was not completed:", error);
    }
  }, [analyticsContext, analyticsVehicleData, dealerData, pricing, vehicleName]);
  return {
    handleSave,
    handleShare,
    isPending,
    isSaved
  };
}
export function VehicleSaveIconButton(t0) {
  const $ = _c(7);
  const {
    vehicleData,
    dealerData
  } = t0;
  const {
    handleSave,
    isPending,
    isSaved
  } = useVehicleActions(vehicleData, dealerData);
  const t1 = `${styles.photoSave} ${isSaved ? styles.photoSaveActive : ""}`;
  const t2 = isSaved ? "Remove from saved vehicles" : "Save vehicle";
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Heart aria-hidden="true" />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== handleSave || $[2] !== isPending || $[3] !== isSaved || $[4] !== t1 || $[5] !== t2) {
    t4 = <button type="button" onClick={handleSave} disabled={isPending} className={t1} aria-label={t2} aria-pressed={isSaved}>{t3}</button>;
    $[1] = handleSave;
    $[2] = isPending;
    $[3] = isSaved;
    $[4] = t1;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
export default function VehicleActions(t0) {
  const $ = _c(16);
  const {
    vehicleData,
    dealerData
  } = t0;
  const {
    handleSave,
    handleShare,
    isPending,
    isSaved
  } = useVehicleActions(vehicleData, dealerData);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Share2 aria-hidden="true" />;
    t2 = <span>Share</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== handleShare) {
    t3 = <button type="button" onClick={handleShare} className={styles.actionButton}>{t1}{t2}</button>;
    $[2] = handleShare;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = `${styles.actionButton} ${isSaved ? styles.actionButtonSaved : ""}`;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Heart aria-hidden="true" />;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = isSaved ? "Saved" : "Save";
  let t7;
  if ($[5] !== t6) {
    t7 = <span>{t6}</span>;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== handleSave || $[8] !== isPending || $[9] !== isSaved || $[10] !== t4 || $[11] !== t7) {
    t8 = <button type="button" onClick={handleSave} disabled={isPending} className={t4} aria-pressed={isSaved}>{t5}{t7}</button>;
    $[7] = handleSave;
    $[8] = isPending;
    $[9] = isSaved;
    $[10] = t4;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t8) {
    t9 = <div className={styles.quickActions} aria-label="Vehicle actions">{t3}{t8}</div>;
    $[13] = t3;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}